import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';

export type PatientReportTextType = {
  id: string;
  name: string;
  createdBy: string;
};

export type PatientReportText = {
  id: string;
  typeId: string;
  body: string;
};

const typesKey = (clinicId: string | null | undefined) =>
  ['patient-reports', 'text-types', clinicId ?? null] as const;

const textsKey = (clinicId: string | null | undefined) =>
  ['patient-reports', 'texts', clinicId ?? null] as const;

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : '';
}

export function patientReportTextErrorMessage(
  error: unknown,
  fallback: string,
  labels: {
    duplicate: string;
    cpr: string;
  },
): string {
  const message = messageOf(error);
  if (message.includes('patient_report_texts_no_cpr')) return labels.cpr;
  if (
    message.includes('patient_report_text_types_clinic_name') ||
    message.includes('duplicate key value')
  ) {
    return labels.duplicate;
  }
  return fallback;
}

export function usePatientReportTextLibrary(clinicId: string | null | undefined) {
  const { user, isClinicAdmin, isPlatformAdmin } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;

  const typesQuery = useQuery({
    queryKey: typesKey(clinicId),
    enabled: Boolean(clinicId),
    staleTime: 30_000,
    queryFn: async (): Promise<PatientReportTextType[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('patient_report_text_types')
        .select('id, name, created_by')
        .eq('clinic_id', clinicId)
        .order('name', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []).map((row) => ({
        id: row.id,
        name: row.name,
        createdBy: row.created_by,
      }));
    },
  });

  const textsQuery = useQuery({
    queryKey: textsKey(clinicId),
    enabled: Boolean(clinicId && userId),
    staleTime: 30_000,
    queryFn: async (): Promise<PatientReportText[]> => {
      if (!clinicId || !userId) return [];
      const { data, error } = await supabase
        .from('patient_report_texts')
        .select('id, type_id, body')
        .eq('clinic_id', clinicId)
        .eq('user_id', userId);
      if (error) throw new Error(error.message);
      return (data ?? []).map((row) => ({
        id: row.id,
        typeId: row.type_id,
        body: row.body,
      }));
    },
  });

  const invalidate = () => {
    if (!clinicId) return;
    void queryClient.invalidateQueries({ queryKey: typesKey(clinicId) });
    void queryClient.invalidateQueries({ queryKey: textsKey(clinicId) });
  };

  const createType = useMutation({
    mutationFn: async (name: string): Promise<PatientReportTextType> => {
      if (!clinicId) throw new Error('No active clinic');
      const trimmed = name.trim();
      if (!trimmed) throw new Error('name');
      const { data, error } = await supabase
        .from('patient_report_text_types')
        .insert({ clinic_id: clinicId, name: trimmed })
        .select('id, name, created_by')
        .single();
      if (error) throw new Error(error.message);
      return { id: data.id, name: data.name, createdBy: data.created_by };
    },
    onSuccess: invalidate,
  });

  const renameType = useMutation({
    mutationFn: async (input: { id: string; name: string }): Promise<void> => {
      if (!clinicId) throw new Error('No active clinic');
      const trimmed = input.name.trim();
      if (!trimmed) throw new Error('name');
      const { error } = await supabase
        .from('patient_report_text_types')
        .update({ name: trimmed })
        .eq('id', input.id)
        .eq('clinic_id', clinicId);
      if (error) throw new Error(error.message);
    },
    onSuccess: invalidate,
  });

  const deleteType = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!clinicId) throw new Error('No active clinic');
      const { error } = await supabase
        .from('patient_report_text_types')
        .delete()
        .eq('id', id)
        .eq('clinic_id', clinicId);
      if (error) throw new Error(error.message);
    },
    onSuccess: invalidate,
  });

  const saveText = useMutation({
    mutationFn: async (input: { typeId: string; body: string }): Promise<void> => {
      if (!clinicId || !userId) throw new Error('No active clinic');
      const { error } = await supabase.from('patient_report_texts').upsert(
        {
          clinic_id: clinicId,
          user_id: userId,
          type_id: input.typeId,
          body: input.body.slice(0, 20000),
        },
        { onConflict: 'clinic_id,user_id,type_id' },
      );
      if (error) throw new Error(error.message);
    },
    onSuccess: invalidate,
  });

  const deleteText = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!clinicId) throw new Error('No active clinic');
      const { error } = await supabase
        .from('patient_report_texts')
        .delete()
        .eq('id', id)
        .eq('clinic_id', clinicId);
      if (error) throw new Error(error.message);
    },
    onSuccess: invalidate,
  });

  return {
    types: typesQuery.data ?? [],
    texts: textsQuery.data ?? [],
    isLoading: typesQuery.isLoading || textsQuery.isLoading,
    error: typesQuery.error ?? textsQuery.error,
    canManageType: (type: { createdBy: string }) =>
      isPlatformAdmin || type.createdBy === userId || (clinicId ? isClinicAdmin(clinicId) : false),
    createType: createType.mutateAsync,
    renameType: renameType.mutateAsync,
    deleteType: deleteType.mutateAsync,
    saveText: saveText.mutateAsync,
    deleteText: deleteText.mutateAsync,
    isSaving:
      createType.isPending ||
      renameType.isPending ||
      deleteType.isPending ||
      saveText.isPending ||
      deleteText.isPending,
  };
}
